import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  UserCheck,
  FileText,
  BarChart3,
  Shield,
  Users,
  Activity,
  Clock,
  Star,
  Plus
} from 'lucide-react';
import { OverviewPageLayout } from '../../components/ui/OverviewPage';
import { AddDriverModal } from '../../components/modals/AddDriverModal';


export function DriverOverviewPage() {
  const [showAddModal, setShowAddModal] = useState(false);
  const driverStats = useSelector(state => state.drivers);
  
  const formatNumber = (num) => num.toLocaleString();
  const formatPercentage = (num) => `${num.toFixed(1)}%`;

  const kpiCards = [
    {
      title: 'Total Drivers',
      value: driverStats?.drivers?.length || 0,
      change: 12.5,
      icon: Users,
      color: 'bg-blue-50 border-blue-200',
      iconColor: 'text-blue-600',
      isCount: true
    },
    {
      title: 'Active Drivers',
      value: driverStats?.drivers?.filter(d => d.status === 'active')?.length || 0,
      change: 8.3,
      icon: Activity,
      color: 'bg-green-50 border-green-200',
      iconColor: 'text-green-600',
      isCount: true
    },
    {
      title: 'Pending Applications',
      value: driverStats?.pendingRegistrations?.length || 0,
      change: -5.2,
      icon: Clock,
      color: 'bg-orange-50 border-orange-200',
      iconColor: 'text-orange-600',
      isCount: true
    },
    {
      title: 'Average Rating',
      value: 4.7,
      change: 2.1,
      icon: Star,
      color: 'bg-purple-50 border-purple-200',
      iconColor: 'text-purple-600',
      formatValue: (value) => `${value.toFixed(1)} ⭐`
    }
  ];

  const quickActions = [
    {
      title: 'Add New Driver',
      description: 'Register a new driver to the platform',
      icon: Plus,
      onClick: () => setShowAddModal(true),
      color: 'bg-green-50 border-green-200',
      iconColor: 'text-green-600'
    },
    {
      title: 'Driver Applications',
      description: 'Review and process new driver applications',
      icon: UserCheck,
      link: '/drivers/applications',
      color: 'bg-blue-50 border-blue-200',
      iconColor: 'text-blue-600'
    },
    {
      title: 'Driver Verification',
      description: 'Verify driver documents and background checks',
      icon: Shield,
      link: '/drivers/verification',
      color: 'bg-green-50 border-green-200',
      iconColor: 'text-green-600'
    },
    {
      title: 'Performance Analytics',
      description: 'Monitor driver performance and ratings',
      icon: BarChart3,
      link: '/drivers/performance',
      color: 'bg-purple-50 border-purple-200',
      iconColor: 'text-purple-600'
    },
    {
      title: 'Document Management',
      description: 'Manage driver licenses and certifications',
      icon: FileText,
      link: '/drivers/documents',
      color: 'bg-orange-50 border-orange-200',
      iconColor: 'text-orange-600'
    }
  ];

  const summaryItems = [
    {
      value: formatNumber(driverStats?.drivers?.filter(d => d.status === 'approved')?.length || 0),
      label: 'Approved Drivers',
      color: 'text-green-600'
    },
    {
      value: formatNumber(driverStats?.drivers?.filter(d => d.status === 'suspended')?.length || 0),
      label: 'Suspended Drivers',
      color: 'text-red-600'
    },
    {
      value: formatPercentage(85.2),
      label: 'Approval Rate',
      color: 'text-blue-600'
    }
  ];

  const summarySection = (
    <>
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Driver Summary</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {summaryItems.map((item, index) => (
          <div key={index} className="text-center">
            <div className={`text-3xl font-bold ${item.color}`}>
              {item.value}
            </div>
            <p className="text-sm text-gray-600 mt-1">{item.label}</p>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <>
      <OverviewPageLayout
        title="Driver Management"
        subtitle="Comprehensive driver onboarding and management dashboard"
        kpiCards={kpiCards}
        quickActions={quickActions}
        summarySection={summarySection}
      />



      <AddDriverModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />
    </>
  );
}
